import React, { createContext, useContext, useState, ReactNode } from 'react';

interface TimeFormatContextType {
  use24HourFormat: boolean;
  toggleTimeFormat: () => void;
}

const TimeFormatContext = createContext<TimeFormatContextType | undefined>(undefined);

/**
 * Detect if the user's locale typically uses 24-hour time format.
 * UK/Europe use 24h, US/Canada typically use 12h.
 */
function detectLocaleUses24Hour(): boolean {
  try {
    // Use Intl API to check if the locale uses 12-hour format
    const options = new Intl.DateTimeFormat(undefined, { hour: 'numeric' }).resolvedOptions();
    // hour12 is true for 12-hour locales (US), false/undefined for 24-hour locales (UK/EU)
    return options.hour12 === false || options.hour12 === undefined;
  } catch {
    // Fallback to 24h if detection fails
    return true;
  }
}

export const TimeFormatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [use24HourFormat, setUse24HourFormat] = useState(() => {
    const saved = localStorage.getItem('timeFormat');
    if (saved) {
      // User has explicitly set a preference
      return saved === '24';
    }
    // First visit: auto-detect from browser locale
    return detectLocaleUses24Hour();
  });

  const toggleTimeFormat = () => {
    setUse24HourFormat((prev) => {
      const newFormat = !prev;
      localStorage.setItem('timeFormat', newFormat ? '24' : '12');
      return newFormat;
    });
  };

  return (
    <TimeFormatContext.Provider value={{ use24HourFormat, toggleTimeFormat }}>
      {children}
    </TimeFormatContext.Provider>
  );
};

export const useTimeFormat = () => {
  const context = useContext(TimeFormatContext);
  if (!context) {
    throw new Error('useTimeFormat must be used within TimeFormatProvider');
  }
  return context;
};
